import * as THREE from 'three';
import type { CountryData } from './countries';

/**
 * Returns the scene-local centroid of the largest polygon across a set of sovereign territories.
 * "Largest" is approximated by vertex count of the outer ring.
 * The caller is responsible for any coordinate-space transforms (e.g. scene.scale.z = -1).
 */
export function largestPolygonCentroid(countries: CountryData[], radius: number): THREE.Vector3 | null {
    let bestRing: GeoJSON.Position[] | null = null;
    let bestLen = 0;

    for (const country of countries) {
        const geom = country.geometry;
        if (geom.type === 'Polygon') {
            if (geom.coordinates[0].length > bestLen) {
                bestLen = geom.coordinates[0].length;
                bestRing = geom.coordinates[0];
            }
        } else if (geom.type === 'MultiPolygon') {
            for (const poly of geom.coordinates) {
                if (poly[0].length > bestLen) {
                    bestLen = poly[0].length;
                    bestRing = poly[0];
                }
            }
        }
    }

    if (!bestRing) return null;

    let sumLat = 0, sumLng = 0;
    for (const [lng, lat] of bestRing) { sumLat += lat; sumLng += lng; }

    return latLonToPoint3D(sumLat / bestRing.length, sumLng / bestRing.length, radius);
}

/**
 * Converts geographic coordinates to a 3D point on a sphere.
 */
export function latLonToPoint3D(latitude: number, longitude: number, radius: number): THREE.Vector3 {
    const latRad = latitude * (Math.PI / 180);
    const lonRad = longitude * (Math.PI / 180);
    return new THREE.Vector3(
        radius * Math.cos(latRad) * Math.cos(lonRad),
        radius * Math.sin(latRad),
        radius * Math.cos(latRad) * Math.sin(lonRad),
    );
}
